import {
  GerminationRoom,
  PaginationData,
} from 'constants/interface';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

export interface CreateGerminationRoomData extends Omit<
  GerminationRoom,
  'id' |
  'createdAt' |
  'updatedAt' |
  'createdBy' |
  'updatedBy'
> {}

export interface UpdateGerminationRoomData {
  id: string
  data: CreateGerminationRoomData
}

export interface GerminationRoomApi {
  createGerminationRoom(germinationRoomData: CreateGerminationRoomData): Promise<GerminationRoom>
  updateGerminationRoom(germinationRoomData: UpdateGerminationRoomData): Promise<GerminationRoom>
  getGerminationRoom(germinationRoomId: string): Promise<GerminationRoom>
  getGerminationRoomList(filter: PaginationData): Promise<PaginatedResponse<GerminationRoom>>
}

const germinationRoomApiBuilder = (): GerminationRoomApi => {
  const createGerminationRoom = async (
    germinationRoomData: CreateGerminationRoomData,
  ): Promise<GerminationRoom> => {
    const params: Record<string, unknown> = {
      ...germinationRoomData,
    };
    const result = await requestClient.post('/germination-rooms', params);

    const createdGerminationRoom: GerminationRoom = result.data as GerminationRoom;
    return createdGerminationRoom;
  };

  const updateGerminationRoom = async (
    germinationRoomData: UpdateGerminationRoomData,
  ): Promise<GerminationRoom> => {
    const { id: germinationRoomId, data } = germinationRoomData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/germination-rooms/${germinationRoomId}`, params);
    const updatedGerminationRoom: GerminationRoom = result.data as GerminationRoom;
    return updatedGerminationRoom;
  };

  const getGerminationRoom = async (
    germinationRoomId: string,
  ): Promise<GerminationRoom> => {
    const result = await requestClient.get(`/germination-rooms/${germinationRoomId}`);
    const germinationRoom: GerminationRoom = result.data as GerminationRoom;
    return germinationRoom;
  };

  const getGerminationRoomList = async (
    filterData: PaginationData,
  ): Promise<PaginatedResponse<GerminationRoom>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/germination-rooms', params);
    const pagination = getPaginationHeaders(result);
    const germinationRoomList: GerminationRoom[] = result.data as GerminationRoom[];
    return {
      pagination,
      data: germinationRoomList,
    };
  };

  return {
    createGerminationRoom,
    updateGerminationRoom,
    getGerminationRoom,
    getGerminationRoomList,
  };
};

export const germinationRoomApi = germinationRoomApiBuilder();
